<template>
  <div class="wizard-holder">
    <div v-if="wizardInProgress && !questionnaireCompleted" class="wizard">
      <progress-bar
        :length="length"
        :step="currentStepNumber"
        :progress="progress"
        :translations="translations">
      </progress-bar>

      <div class="explanation"
        v-if="currentQuestion.explanation_translation">
        {{currentQuestion.explanation_translation.translation_entries[0].body}}
      </div>
      <div class="image"
        v-if="currentQuestion.image_path">
        <img :src="currentQuestion.image_path" />
      </div>
      <div class="image-caption"
        v-if="currentQuestion.image_caption_translation">
        {{currentQuestion.image_caption_translation.translation_entries[0].body}}
      </div>
      <hr class="separator"
        v-if="currentQuestion.explanation_translation ||
              currentQuestion.image_path ||
              currentQuestion.image_caption_translation"
      />

        <component
          ref="currentStep"
          v-if="!questionnaireCompleted"
          :is="currentStep"
          :key="currentStepNumber"
          :question="currentQuestion"
          :questionTitle="currentQuestionTitle"
          :wizard-data="form"
          @currentQuestionAnswer="currentQuestionAnswer"
          @answered="setQuestionStatus"
          @update="processStep">
        </component>

      <div v-if="needsComment">
        <h3 class="low-score-comment">{{translations['low-score-comment']}}</h3>
        <rp-textbox
          v-model="comment"
          :limit=1000
          :rows=3 />
      </div>

      <div class="breadcrumb-actions">
        <rp-button
          class="previous-btn"
          :class=" { 'is-inactive': currentStepNumber === 1 }"
          @click="goBack">
          {{translations['previous']}}
        </rp-button>

        <rp-button
          class="next-btn is-primary"
          :disabled="!canGoNext"
          @click="nextButtonAction">
          {{nextButton}}
        </rp-button>
      </div>
    </div>

    <div class="complete" v-if="questionnaireCompleted">
      <p>{{translations['thank-you']}}</p>
    </div>
  </div>
</template>

<script>
import RpButton from 'raypro_ui/src/components/ui/inputs/Button'
import Boolean from './Elements/Boolean'
import Multiple from './Elements/Multiple'
import Range from './Elements/Range'
import Textbox from './Elements/Textbox'
import ProgressBar from './Elements/ProgressBar'
import store from '@/store/'
import {mapGetters} from 'vuex'
import * as authTypes from 'raypro_ui/src/store/modules/auth/types'
import RpTextBox from 'raypro_ui/src/components/ui/inputs/TextBox.vue'

import {createQuestionnaireAnswer, updateQuestionnaireAnswer} from '@/api/questionnaire'

export default {
  components: {
    RpButton,
    Boolean,
    Multiple,
    'textbox': Textbox,
    Range,
    ProgressBar,
    'rp-textbox': RpTextBox
  },

  data() {
    return {
      currentStepNumber: 1,
      canGoNext: false,
      questionnaireCompleted: false,
      stepTypes: [],
      form: [],
      stepAnswer: {},
      patientAnswerId: '',
      currentQuestionAnswered: false,
      comment: ''
    }
  },

  mounted() {
    this.setStepTypes()
    this.setCurrentQuestion()
  },

  props: {
    details: Array,
    questions: Array,
    currentQuestionIndex: Number,
    questionnaireId: String,
    translations: Array
  },

  methods: {
    nextButtonAction() {
      if(this.isLastStep) {
        this.submitSingleAnswer()
        this.completeQuestionnaire()
      } else {
        this.goNext()
      }
    },

    goBack() {
      if(this.currentStepNumber === 1) {
        return false
      }
      this.canGoNext = false
      this.currentQuestionAnswer()
      this.currentStepNumber--
      this.setQuestionParam()
    },

    goNext() {
      this.canGoNext = false
      this.submitSingleAnswer()
      this.currentStepNumber++
      this.setQuestionParam()
    },

    setQuestionParam() {
      this.$router.push({query: {jwt: this.$route.query.jwt, question: this.currentStepNumber, eye: this.$route.query.eye}})
      this.currentQuestionAnswer()
    },

    processStep(step) {
      const index = this.form.findIndex((item) => {
        return item.id === step.id
      })

      this.stepAnswer = step

      if(index === -1) {
        this.form.push(step)
      } else {
        this.form[index] = step
      }

      this.$emit('updateStepNumber', this.currentStepNumber)
      this.canGoNext = true
    },

    completeQuestionnaire() {
      if(!this.authUser) {
        store.commit(authTypes.TOKEN, null)
      }
      this.questionnaireCompleted = true
      this.$emit('onQuestionnaireCompleted')
    },

    setQuestionStatus(status) {
      this.currentQuestionAnswered = status
    },

    setStepTypes() {
      const types = []
      const textType = 'text'

      this.questions.forEach((question) => {
        if(question.question_type.name === textType) {
          types.push('textbox')
        } else {
          types.push(question.question_type.name)
        }
      })

      this.stepTypes = types
      // this.stepTypes = ['boolean','boolean','boolean']
    },

    setCurrentQuestion() {
      // This reads the url params and loads the correct question
      this.currentStepNumber = parseInt(this.currentQuestionIndex)
    },

    submitSingleAnswer() {
      const answer = this.stepAnswer

      // Value comes out the DB as a string but for range type questions the API
      // expects an integer so convert it if necessary
      const value = (this.currentStep === 'range' && typeof answer.value === 'string') ?
        parseInt(answer.value) : answer.value

      const postData = {
        patient_questionnaire_answer: {
          value: value,
          patient_questionnaire_id: this.questionnaireId
        }
      }

      const updateData = {patient_questionnaire_answer: {value: value}}

      if (this.needsComment && this.comment.trim().length > 0) {
        postData.patient_questionnaire_answer.comment = updateData.patient_questionnaire_answer.comment
          = this.comment.trim()
      } else {
        postData.patient_questionnaire_answer.comment = updateData.patient_questionnaire_answer.comment = null
      }

      // If current question has been answered, update the answer
      if(this.currentQuestionAnswered) {
        this.updateSingleAnswer(updateData)
      } else {
        this.createSingleAnswer(answer, postData)
      }
    },

    createSingleAnswer(question, data) {
      createQuestionnaireAnswer(question.id, data)
        .then(() => {
        })
        .catch(() => {
        })
    },

    updateSingleAnswer(data) {
      const answerId = this.patientAnswerId

      updateQuestionnaireAnswer(answerId, data)
        .then(() => {
        })
        .catch(() => {
        })
    },

    currentQuestionAnswer(data) {
      if(data) {
        this.patientAnswerId = data.id
        this.stepAnswer = data
        this.comment = (data.comment === null) ? '' : data.comment

        // Set the next button true if the question has already been answered
        if(this.currentQuestionAnswered) {
          this.canGoNext = true
        }
      } else {
        this.patientAnswerId = null
        this.stepAnswer = null
        this.canGoNext = false
        this.currentQuestionAnswered = false
        this.comment = ''
      }
    }
  },

  computed: {
    ...mapGetters(['authUser']),

    isLastStep() {
      return this.currentStepNumber === this.length
    },

    nextButton() {
      return this.isLastStep ? this.translations.finish : this.translations['next-question']
    },

    wizardInProgress() {
      return this.currentStepNumber <= this.length
    },

    length() {
      return this.questions.length
    },

    currentStep() {
      const offset = -1

      return this.stepTypes[this.currentStepNumber + offset]
    },

    progress() {
      const max = 100

      return this.currentStepNumber / this.length * max
    },

    currentQuestion: {
      get() {
        return this.questions[this.currentStepNumber - 1]
      },

      set() {
        return this.questions[this.currentQuestionIndex]
      }
    },

    currentQuestionTitle() {
      return this.currentQuestion.translation.translation_entries[0].body
    },

    needsComment() {
      return this.currentStep === 'range' && this.currentQuestion.topic.toLowerCase().includes('satisfaction')
        && this.stepAnswer && this.stepAnswer.value < 7
    }
  }
}
</script>

<style lang="sass">
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'

  .breadcrumb-actions
    display: flex
    flex-direction: column-reverse
    @include media($media-medium)
      flex-direction: row
      justify-content: space-between
      width: 100%

  .wizard-holder
    position: relative
    background-color: $color-panel-bg
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1)
    margin: 75px 0

    @include media($media-medium)
      margin: 0

    .title
      width: 100%

    .wizard
      padding: 20px

      .explanation
        font-size: medium;
        margin-bottom: 20px
      .image
        img
          width: 100%
      .image-caption
        font-size: small
        font-style: italic
      .separator
        border: 0
        height: 1px
        margin-top: 30px
        margin-bottom: -20px
        background-color: $color-primary

      .actions
        padding-top: 20px
        display: flex
        flex-direction: row

        .next-btn
          margin-left: auto

        .previous-btn
          border: 1px solid rgba(0, 0, 0, 0.25)
          color: grey

        a
          cursor: pointer
          font-weight: $font-light
          color: $color-notification
          text-decoration: underline

    .btn
      padding: 15px 15px
      line-height: 0
      font-size: 14px

      &.is-inactive
        opacity: 0

  .complete
    text-align: center
    padding: 20px

  .low-score-comment
    text-transform: none
</style>



// WEBPACK FOOTER //
// src/components/pages/Questionnaire/QuestionsWizard.vue